import { useCallback, useEffect, useState } from "react";

interface EditableListState<T> {
  added: T[];
  updated: Record<string, Partial<T>>;
  deleted: Set<string>;
}

/**
 * Pure editable list hook - only handles change tracking and local state
 * Data fetching and persistence are handled externally
 * @param initialItems - Initial data items
 */
export function useEditableList<T extends { id: string }>(
  initialItems: T[] = [],
  options?: { append?: boolean }
) {
  // Local state for items with changes applied
  const [items, setItems] = useState<T[]>(initialItems);

  // Changes tracking
  const [changes, setChanges] = useState<EditableListState<T>>({
    added: [],
    updated: {},
    deleted: new Set(),
  });

  // Sync items when initial data changes
  useEffect(() => {
    if (options?.append && initialItems.length > 0) {
      setItems(prev => [
        ...prev,
        ...initialItems.filter(ex => !prev.some(p => p.id === ex.id)), // dedupe by id
      ]);
    } else {
      setItems(initialItems);
    }
  }, [initialItems, options?.append]);

  // ---- Editing Functions ----
  const addItem = useCallback((item: T) => {
    setChanges((prev) => ({
      ...prev,
      added: [...prev.added, item],
    }));
    setItems((prev) => [item, ...prev]);
  }, []);

  const updateItem = useCallback((id: string, updatedFields: Partial<T>) => {
    setChanges((prev) => {
      // Check if the item being updated is in the 'added' array
      const addedIndex = prev.added.findIndex((item) => item.id === id);
      
      if (addedIndex !== -1) {
        // Update the item in the 'added' array instead of 'updated'
        const updatedAdded = [...prev.added];
        updatedAdded[addedIndex] = { ...prev.added[addedIndex], ...updatedFields };
        return {
          ...prev,
          added: updatedAdded,
        };
      }
      
      // Otherwise, add to 'updated' as usual
      return {
        ...prev,
        updated: {
          ...prev.updated,
          [id]: { ...prev.updated[id], ...updatedFields },
        },
      };
    });
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updatedFields } : item))
    );
  }, []);

  const deleteItem = useCallback((id: string) => {
    setChanges((prev) => ({
      ...prev,
      deleted: new Set(Array.from(prev.deleted).concat(id)),
    }));
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const resetChanges = useCallback(() => {
    setChanges({ added: [], updated: {}, deleted: new Set() });
    setItems(initialItems); // Reset to original data
  }, [initialItems]);

  const clearChanges = useCallback(() => {
    // Clear change tracking without resetting items
    // Useful when you want to keep optimistic updates until new data arrives
    setChanges({ added: [], updated: {}, deleted: new Set() });
  }, []);

  // Check if there are any pending changes
  const hasChanges =
    changes.added.length > 0 ||
    Object.keys(changes.updated).length > 0 ||
    changes.deleted.size > 0;

  // Calculate changes count for UI
  const changesCount =
    changes.added.length +
    Object.keys(changes.updated).length +
    changes.deleted.size;

  return {
    // Data
    items,

    // Changes
    changes,
    hasChanges,
    changesCount,

    // Actions
    addItem,
    updateItem,
    deleteItem,
    resetChanges,
    clearChanges,
  };
}
